import React, { useCallback, useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import LoadFailed from '../shared/load_failed';
import TpDialog from '../shared/tp_dialog';
import { fetch_close_versions_teacher_month } from '../api';
import { changed_lines, cv_error_view, teacher_month_view } from './adapters';

/*
 * one teacher-month's versions (v1, v2, …, plus "Live now" while reopened), each against the one
 * before it, per component. Wide: every version in one table; narrow: one pair at a time.
 */
const LOAD_FAILED_TEXT = 'Could not load the versions of this teacher-month.';

const SOURCE_HINTS = {
  rebuilt_from_history: 'Rebuilt from the last recalculation before this close.',
  net_only: 'Only the net payable was recorded for this close; the breakdown is not available.'
};

const cell_class = (change, key) =>
  change && change.lines[key].changed ? 'tp-cv-changed' : undefined;

const WhatChanged = ({ change }) => {
  if (!change) return <span className="tp-hint">First close</span>;
  const lines = changed_lines(change);
  if (!lines.length)
    return <span className="tp-hint">No change against {change.baseline_label}</span>;
  return (
    <ul className="tp-cv-changes" aria-label={`Changed against ${change.baseline_label}`}>
      {lines.map((l) => (
        <li key={l.key}>
          {l.label} {l.text}
        </li>
      ))}
    </ul>
  );
};

const VersionCard = ({ col }) => (
  <div className="tp-cv-version" data-version={col.key}>
    <div className="tp-cv-version-hd">
      <b>{col.label}</b>
      <span className="tp-cv-source">{col.source_label}</span>
    </div>
    {SOURCE_HINTS[col.source] ? <div className="tp-hint">{SOURCE_HINTS[col.source]}</div> : null}
    {col.meta.map((line) => (
      <div key={line} className="tp-cv-meta">
        {line}
      </div>
    ))}
    <WhatChanged change={col.change} />
  </div>
);

// With a pair to show, narrow screens swap this table for PairView; a single version keeps it.
const AllVersionsTable = ({ view }) => (
  <div className={`tp-tablewrap${view.pairs.length ? ' tp-cv-full' : ''}`}>
    <table className="tp-table">
      <thead>
        <tr>
          <th scope="col">Line</th>
          {view.columns.map((c) => (
            <React.Fragment key={c.key}>
              <th scope="col" style={{ textAlign: 'right' }}>
                {c.label}
              </th>
              {c.change ? (
                <th scope="col" style={{ textAlign: 'right' }}>
                  Change vs {c.change.baseline_label}
                </th>
              ) : null}
            </React.Fragment>
          ))}
        </tr>
      </thead>
      <tbody>
        {view.lines.map((l) => (
          <tr key={l.key}>
            <th scope="row">{l.label}</th>
            {view.columns.map((c) => (
              <React.Fragment key={c.key}>
                <td style={{ textAlign: 'right' }} className={cell_class(c.change, l.key)}>
                  {c.values[l.key]}
                </td>
                {c.change ? (
                  <td style={{ textAlign: 'right' }} className={cell_class(c.change, l.key)}>
                    {c.change.lines[l.key].text}
                  </td>
                ) : null}
              </React.Fragment>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PairView = ({ view }) => {
  const [pair_id, setPairId] = useState(view.pairs.length ? view.pairs[0].id : '');
  const pair = view.pairs.find((p) => p.id === pair_id) || view.pairs[0];
  if (!pair) return null;
  const { before, after } = pair;
  return (
    <div className="tp-cv-pair">
      {view.pairs.length > 1 ? (
        <label className="tp-cv-pairpick">
          <span className="tp-hint">Compare</span>
          <select className="tp-select" value={pair.id} onChange={(e) => setPairId(e.target.value)}>
            {view.pairs.map((p) => (
              <option key={p.id} value={p.id}>
                {p.after.label} with {p.before.label}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <div className="tp-tablewrap">
        <table className="tp-table tp-cv-pair-table">
          <thead>
            <tr>
              <th scope="col">Line</th>
              <th scope="col" style={{ textAlign: 'right' }}>
                {before.label}
              </th>
              <th scope="col" style={{ textAlign: 'right' }}>
                {after.label}
              </th>
              <th scope="col" style={{ textAlign: 'right' }}>
                Change
              </th>
            </tr>
          </thead>
          <tbody>
            {view.lines.map((l) => (
              <tr key={l.key}>
                <th scope="row">{l.label}</th>
                <td style={{ textAlign: 'right' }}>{before.values[l.key]}</td>
                <td style={{ textAlign: 'right' }} className={cell_class(after.change, l.key)}>
                  {after.values[l.key]}
                </td>
                <td style={{ textAlign: 'right' }} className={cell_class(after.change, l.key)}>
                  {after.change.lines[l.key].text}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const VersionsDialog = ({
  pay_month,
  month_label,
  teacher_uuid,
  teacher_name,
  on_close,
  on_focus_lost,
  on_error
}) => {
  const [state, setState] = useState('loading'); // loading | ready | error
  const [view, setView] = useState(null);
  const [error_text, setErrorText] = useState('');
  const seq = useRef(0);
  // The callers' callbacks are read through refs so a re-render never re-reads the versions.
  const on_close_ref = useRef(on_close);
  on_close_ref.current = on_close;
  const on_error_ref = useRef(on_error);
  on_error_ref.current = on_error;

  const load = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    setState('loading');
    fetch_close_versions_teacher_month(pay_month, teacher_uuid)
      .then((data) => {
        if (mine !== seq.current) return;
        setView(teacher_month_view(data));
        setState('ready');
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        const v = cv_error_view(e, LOAD_FAILED_TEXT);
        if (v.kind === 'permission') {
          on_error_ref.current(e, LOAD_FAILED_TEXT);
          on_close_ref.current();
          return;
        }
        setErrorText(v.text);
        setState('error');
      });
  }, [pay_month, teacher_uuid]);

  useEffect(() => {
    load();
  }, [load]);

  const name = (view && view.teacher_name) || teacher_name || 'Teacher';
  const title = `${name} · ${month_label}`;

  return (
    <TpDialog title={title} on_close={on_close} on_focus_lost={on_focus_lost}>
      <div className="tp-cv-dialog">
        {state === 'loading' ? <CustomLoading /> : null}
        {state === 'error' ? <LoadFailed text={error_text} on_retry={load} /> : null}
        {state === 'ready' && !view.columns.length ? (
          <div className="tp-empty">
            No close is recorded for {name} in {month_label}.
          </div>
        ) : null}
        {state === 'ready' && view.columns.length ? (
          <>
            <div className="tp-cv-versions">
              {view.columns.map((c) => (
                <VersionCard key={c.key} col={c} />
              ))}
            </div>
            <AllVersionsTable view={view} />
            <PairView view={view} />
          </>
        ) : null}
      </div>
    </TpDialog>
  );
};

export default VersionsDialog;
